import { cloneDeep, uniq } from "lodash-es";
import {
    type ComponentProps,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import {
    BdsIconAttach,
    BdsIconComments,
    BdsIconHomeowners,
    BdsIconMention,
    BdsIconReplyClockwise,
    BdsIconSubsVendors,
    BdsIconThumbsUp,
    BdsIconUsers,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { getSelectedValues } from "utilities/form/form";
import { isNullOrUndefined } from "utilities/object/object";
import { PortalType } from "utilities/portal/portal";

import { BTTabs } from "commonComponents/btWrappers/BTTabs/BTTabs";
import { CommentPermissions } from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import {
    type CommentTabNames,
    type IHasContent,
    includeUser,
    scanForMentions,
} from "commonComponents/entity/comment/CommentContainer/CommentContainer.utils";
import {
    type DiscussionsEntity,
    type IHasDiscussionContainer,
    type ParticipantsResponse,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { EmptyStateCell } from "commonComponents/entity/emptyState/EmptyStateCell";
import { PortalInfoContext } from "commonComponents/utilities/InlineOwnerPreview/PortalInfoContext";

import "./CommentTabs.less";

interface ICommentTabsProps {
    entity: IHasDiscussionContainer;
    tabsVisible: BTLoginTypes[];
    children: (
        entity: IHasDiscussionContainer,
        systemMessage?: React.ReactNode,
        emptyState?: React.ReactNode,
        filterParticipants?: (
            participants?: ParticipantsResponse,
        ) => ParticipantsResponse | undefined,
        updateState?: (newDiscussions: DiscussionsEntity) => void,
        activeTab?: BTLoginTypes,
    ) => React.ReactNode;
    updateParentDiscussionState: (newDiscussions: DiscussionsEntity) => Promise<void>;
    discussionId?: number | null;
    /** Fallback tab visibility used when addNewCommentPermissions is null (new/unsaved entity). */
    defaultTabVisibility?: { canShowOwner?: boolean; canShowSubs?: boolean };
}

export default function CommentTabs({
    entity,
    children,
    tabsVisible,
    updateParentDiscussionState,
    discussionId,
    defaultTabVisibility,
}: ICommentTabsProps) {
    const unreadAnimationDelay = 200;
    const [currentTab, setCurrentTab] = useState<BTLoginTypes | undefined>(undefined);
    const userInfo = useRequiredContext(UserInfoContext);
    const portalInfo = useRequiredContext(PortalInfoContext);
    const [readTabs, setReadTabs] = useState<BTLoginTypes[]>([]);
    const readTabsSafe = useRef<BTLoginTypes[]>([]);
    const builderInfo = useRequiredContext(BuilderInfoContext);

    const setUnread = useCallback((tab: BTLoginTypes) => {
        const timeout = setTimeout(() => {
            if (readTabsSafe.current.includes(tab)) {
                return;
            }
            readTabsSafe.current = [...readTabsSafe.current, tab];
            setReadTabs((prev) => [...prev, tab]);
        }, unreadAnimationDelay);
        return () => {
            clearInterval(timeout);
        };
    }, []);

    interface ITabbedCommentData {
        builder?: IHasDiscussionContainer;
        subs?: IHasDiscussionContainer;
        clients?: IHasDiscussionContainer;
        showTabs?: boolean;
        currentTab: BTLoginTypes;
    }

    const tabbedCommentData = useMemo(() => {
        const results: ITabbedCommentData = { currentTab: BTLoginTypes.BUILDER };

        const isBuilder = userInfo.loginType === BTLoginTypes.BUILDER;

        const { canShowOwner, canShowSubs } =
            isBuilder && entity.discussions.addNewCommentPermissions
                ? entity.discussions.addNewCommentPermissions
                : {
                      canShowOwner: defaultTabVisibility?.canShowOwner ?? true,
                      canShowSubs: defaultTabVisibility?.canShowSubs ?? true,
                  };
        let count = 0;
        if (tabsVisible.includes(BTLoginTypes.BUILDER)) {
            const { newDiscussionsEntity } = getNewDiscussionsEntity(entity, "internal");
            results.builder = newDiscussionsEntity;
            count++;
        }

        if (tabsVisible.includes(BTLoginTypes.SUBS) && canShowSubs) {
            const { newDiscussionsEntity, isCommentIdFromQSOnCurrentTab } = getNewDiscussionsEntity(
                entity,
                "subs",
                discussionId,
            );
            results.subs = newDiscussionsEntity;
            if (isCommentIdFromQSOnCurrentTab) {
                results.currentTab = BTLoginTypes.SUBS;
            }
            count++;
        }

        if (tabsVisible.includes(BTLoginTypes.OWNER) && canShowOwner) {
            const { newDiscussionsEntity, isCommentIdFromQSOnCurrentTab } = getNewDiscussionsEntity(
                entity,
                "clients",
                discussionId,
            );
            results.clients = newDiscussionsEntity;
            if (isCommentIdFromQSOnCurrentTab) {
                results.currentTab = BTLoginTypes.OWNER;
            }
            count++;
        }

        if (count > 1) {
            results.showTabs = true;
        }

        return results;
    }, [entity, tabsVisible, userInfo.loginType, discussionId, defaultTabVisibility]);

    useEffect(() => {
        if (!currentTab) {
            setCurrentTab(tabbedCommentData.currentTab);
            setUnread(tabbedCommentData.currentTab);
        }
    }, [currentTab, setUnread, tabbedCommentData]);

    const tabWrapper = useCallback(
        (children: React.ReactNode) =>
            tabbedCommentData.showTabs ? (
                <BTTabs
                    data-testid="comment-tabs"
                    activeKey={currentTab?.toString()}
                    className="CommentTabs"
                    onChange={(activeKey) => {
                        const newTab = parseInt(activeKey) as BTLoginTypes;
                        setCurrentTab(newTab);
                        setUnread(newTab);
                    }}
                >
                    {children}
                </BTTabs>
            ) : (
                <div
                    style={{ padding: 0, borderTop: "var(--bui-token-border-default)" }}
                    className="CommentTabs SubsAndOwnerTab"
                >
                    {children}
                </div>
            ),
        [currentTab, tabbedCommentData.showTabs, setUnread],
    );

    const tabPaneWrapper = useCallback(
        (tab: string, key: string, isUnread: boolean, children: React.ReactNode) =>
            tabbedCommentData.showTabs ? (
                <BTTabs.TabPane
                    className="CommentTabPane"
                    style={{ minHeight: "100%" }}
                    tab={<div className={cn({ IsUnread: isUnread })}>{tab}</div>}
                    key={key}
                >
                    {children}
                </BTTabs.TabPane>
            ) : (
                <>{children}</>
            ),
        [tabbedCommentData.showTabs],
    );

    // Need to add back in the full discussions state before updating the parent
    const updateState = useCallback(
        async (newDiscussions: DiscussionsEntity) => {
            await updateParentDiscussionState({
                ...entity.discussions,
                comments: {
                    ...entity.discussions.comments,
                    comments: [
                        ...newDiscussions.comments.comments,
                        ...entity.discussions.comments.comments.filter(
                            (c) => !newDiscussions.comments.comments.some((com) => com.id === c.id),
                        ),
                    ],
                },
            });
        },
        [entity, updateParentDiscussionState],
    );

    const builderCommentCount =
        tabbedCommentData.builder?.discussions.comments.comments.length ?? 0;
    const subsCommentCount = tabbedCommentData.subs?.discussions.comments.comments.length ?? 0;
    const clientsCommentCount =
        tabbedCommentData.clients?.discussions.comments.comments.length ?? 0;

    const builderCountLabel = builderCommentCount > 0 ? ` (${builderCommentCount})` : "";
    const subsCountLabel = subsCommentCount > 0 ? ` (${subsCommentCount})` : "";
    const clientsCountLabel = clientsCommentCount > 0 ? ` (${clientsCommentCount})` : "";

    const builderIsUnread =
        (tabbedCommentData.builder?.discussions.comments.comments.some((c) => c.isUnread) ??
            false) &&
        !readTabs.includes(BTLoginTypes.BUILDER);
    const clientIsUnread =
        (tabbedCommentData.clients?.discussions.comments.comments.some((c) => c.isUnread) ??
            false) &&
        !readTabs.includes(BTLoginTypes.OWNER);
    const subIsUnread =
        (tabbedCommentData.subs?.discussions.comments.comments.some((c) => c.isUnread) ?? false) &&
        !readTabs.includes(BTLoginTypes.SUBS);

    return tabWrapper(
        <>
            {tabbedCommentData.builder &&
                tabPaneWrapper(
                    `Internal${builderCountLabel}`,
                    BTLoginTypes.BUILDER.toString(),
                    builderIsUnread,
                    children(
                        tabbedCommentData.builder,
                        <SystemMessage
                            text="Comments are visible to internal users only"
                            icons={[<BdsIconUsers key="usersIcon" />]}
                            popoverText="Internal"
                        />,
                        builderCommentCount > 0 ? undefined : <BuilderEmptyState />,
                        (participants) =>
                            filterParticipants(
                                participants,
                                tabbedCommentData.builder,
                                true,
                                false,
                                false,
                            ),
                        updateState,
                        BTLoginTypes.BUILDER,
                    ),
                )}

            {tabbedCommentData.clients &&
                tabPaneWrapper(
                    `Clients${clientsCountLabel}`,
                    BTLoginTypes.OWNER.toString(),
                    clientIsUnread,
                    children(
                        tabbedCommentData.clients,
                        <SystemMessage
                            text={
                                portalInfo.currentPortal === PortalType.OWNER
                                    ? `Comments can be seen by ${builderInfo.name}`
                                    : "Comments are visible to client(s) and internal users"
                            }
                            icons={[
                                <BdsIconHomeowners key="homeownersIcon" />,
                                <BdsIconUsers key="usersIcon" />,
                            ]}
                            popoverText={
                                portalInfo.currentPortal === PortalType.OWNER
                                    ? `All clients and ${builderInfo.name}`
                                    : "Clients and internal"
                            }
                        />,
                        clientsCommentCount > 0 ? undefined : <ClientEmptyState />,
                        (participants) =>
                            filterParticipants(
                                participants,
                                tabbedCommentData.clients,
                                true,
                                true,
                                false,
                            ),
                        updateState,
                        BTLoginTypes.OWNER,
                    ),
                )}

            {tabbedCommentData.subs &&
                tabPaneWrapper(
                    `Subs/vendors${subsCountLabel}`,
                    BTLoginTypes.SUBS.toString(),
                    subIsUnread,
                    children(
                        tabbedCommentData.subs,
                        <SystemMessage
                            text={
                                portalInfo.currentPortal === PortalType.SUBS
                                    ? `Comments are visible to the subs/vendors who have access to this item and ${builderInfo.name}`
                                    : "Comments are visible to subs/vendors and internal users"
                            }
                            icons={[
                                <BdsIconSubsVendors key="subsVendorsIcon" />,
                                <BdsIconUsers key="usersIcon" />,
                            ]}
                            popoverText={
                                portalInfo.currentPortal === PortalType.SUBS
                                    ? `Subs/vendors and \n ${builderInfo.name}`
                                    : "Subs/vendors and internal"
                            }
                        />,
                        subsCommentCount > 0 ? undefined : <SubEmptyState />,
                        (participants) =>
                            filterParticipants(
                                participants,
                                tabbedCommentData.subs,
                                true,
                                false,
                                true,
                            ),
                        updateState,
                        BTLoginTypes.SUBS,
                    ),
                )}
        </>,
    );
}

function SystemMessage({
    text,
    icons,
    popoverText,
}: {
    text: string;
    icons: React.ReactNode[];
    popoverText: string;
}) {
    const tooltipRef = useRef<HTMLDivElement>(null);
    return (
        <div className="SystemMessage bds-gap-1">
            <div ref={tooltipRef} className="SystemMessageText">
                {text}
            </div>
            {icons.length > 0 && (
                <BTPopover content={popoverText}>
                    <div style={{ display: "flex" }}>
                        {icons.map((icon, index) => (
                            <div key={index} className="SystemMessageIcon">
                                {icon}
                            </div>
                        ))}
                    </div>
                </BTPopover>
            )}
        </div>
    );
}

function filterParticipants(
    participants: ParticipantsResponse | undefined,
    entity: IHasDiscussionContainer | undefined,
    showUsers: boolean,
    showClients: boolean,
    showSubs: boolean,
): ParticipantsResponse | undefined {
    if (!participants || !entity) {
        return undefined;
    }
    const notifyUserIds = getSelectedValues(entity.discussions.comments.notificationUsers);
    return {
        ...participants,
        internalParticipants: showUsers
            ? participants.internalParticipants.map((p) => ({
                  ...p,
                  canNotify: notifyUserIds.includes(p.userId),
              }))
            : [],
        clientParticipants: showClients
            ? participants.clientParticipants.map((p) => ({
                  ...p,
                  canNotify: notifyUserIds.includes(p.userId),
              }))
            : [],

        subParticipants: showSubs
            ? participants.subParticipants.map((p) => ({
                  ...p,
                  canNotify: notifyUserIds.includes(p.userId),
              }))
            : [],
    };
}

function getNewDiscussionsEntity(
    baseEntity: IHasDiscussionContainer,
    tab: CommentTabNames,
    discussionId?: number | null,
) {
    const newDiscussionsEntity = {
        ...baseEntity,
        discussions: cloneDeep(baseEntity.discussions),
    };
    let isCommentIdFromQSOnCurrentTab = false;

    // Filter out the comments based on the current tab
    newDiscussionsEntity.discussions.comments.comments =
        newDiscussionsEntity.discussions.comments.comments.filter((c) => {
            const isMatch =
                (tab !== "clients" && !c.showOwner && tab !== "subs" && !c.showSubs) ||
                (tab === "clients" && c.showOwner) ||
                (tab === "subs" && c.showSubs);
            if (!isNullOrUndefined(discussionId) && discussionId === c.id && isMatch) {
                isCommentIdFromQSOnCurrentTab = true;
            }
            return isMatch;
        });

    // Filter out mentionable users based on the current tab
    newDiscussionsEntity.discussions.comments.mentionableUsers =
        newDiscussionsEntity.discussions.comments.mentionableUsers.filter((u) =>
            includeUser(tab, u),
        );

    let participantIds: number[] = [];
    newDiscussionsEntity.discussions.comments.comments.forEach((comment) => {
        // Users who have left a comment or were mentioned
        participantIds.push(comment.addedById);
        const mentionedIds = scanForMentions(
            newDiscussionsEntity.discussions.comments.mentionableUsers,
            comment.commentJson as unknown as IHasContent,
            [],
        );
        participantIds.push(...mentionedIds);
    });
    participantIds = uniq(participantIds);

    // Set the visibility permissions based on current tab.
    // When addNewCommentPermissions is null (unsaved/new entity), create tab-specific
    // permissions so comments posted before the first save land on the correct tab.
    if (newDiscussionsEntity.discussions.addNewCommentPermissions) {
        newDiscussionsEntity.discussions.addNewCommentPermissions.canShowOwner = tab === "clients";
        newDiscussionsEntity.discussions.addNewCommentPermissions.defaultShowOwner =
            tab === "clients";

        newDiscussionsEntity.discussions.addNewCommentPermissions.canShowSubs = tab === "subs";
        newDiscussionsEntity.discussions.addNewCommentPermissions.defaultShowSubs = tab === "subs";
    } else {
        newDiscussionsEntity.discussions.addNewCommentPermissions = new CommentPermissions({
            canDelete: false,
            canShowOwner: tab === "clients",
            canShowSubs: tab === "subs",
            defaultShowOwner: tab === "clients",
            defaultShowSubs: tab === "subs",
        });
    }

    // Filtering out the notify user BTSelectItem options based on the current tab
    const ids: number[] = [];
    newDiscussionsEntity.discussions.comments.notificationUsers =
        newDiscussionsEntity.discussions.comments.notificationUsers
            .map((group) => {
                group.children = group.children?.filter((user) => {
                    const userId = parseInt(user.value);
                    const include =
                        (participantIds.includes(userId) || user.selected) &&
                        includeUser(tab, user);
                    if (include) {
                        user.selected = true;
                        ids.push(userId);
                    }
                    return include;
                });
                return group;
            })
            .filter((group) => (group.children ?? []).length > 0);
    newDiscussionsEntity.discussions.comments.defaultNotifyUsers = ids;

    return {
        newDiscussionsEntity,
        isCommentIdFromQSOnCurrentTab,
    };
}

function BuilderEmptyState() {
    return (
        <CommentsEmptyState
            text="Start a conversation with your internal team only"
            iconRight={<BdsIconUsers size="lg" className="margin-top-md" />}
        />
    );
}

function SubEmptyState() {
    const userInfo = useRequiredContext(UserInfoContext);
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const builderName = builderInfo.name;
    const isSub = userInfo.loginType === BTLoginTypes.SUBS;

    return (
        <CommentsEmptyState
            text={
                isSub
                    ? `Start a conversation with ${builderName}`
                    : "Start a conversation with your subs/vendors and internal team"
            }
            iconLeft={<BdsIconSubsVendors size="lg" className="margin-top-md" />}
            iconRight={<BdsIconUsers size="lg" className="margin-top-md" />}
            visibilityCell={
                !isSub
                    ? undefined
                    : {
                          color: "blue",
                          icon: <BdsIconHomeowners size="sm" />,
                          text: `${builderName}’s client(s) won’t see your comments`,
                      }
            }
        />
    );
}

function ClientEmptyState() {
    const userInfo = useRequiredContext(UserInfoContext);
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const builderName = builderInfo.name;
    const isClient =
        userInfo.loginType === BTLoginTypes.OWNER || userInfo.loginType === BTLoginTypes.CONTACT;
    return (
        <CommentsEmptyState
            text={
                isClient
                    ? `Start a conversation with ${builderName}`
                    : "Start a conversation with your client(s) and internal team"
            }
            iconLeft={<BdsIconHomeowners size="lg" className="margin-top-md" />}
            iconRight={<BdsIconUsers size="lg" className="margin-top-md" />}
            visibilityCell={
                !isClient
                    ? undefined
                    : {
                          color: "blue",
                          icon: <BdsIconSubsVendors size="sm" />,
                          text: `${builderName}’s subs/vendors won’t see your comments`,
                      }
            }
        />
    );
}

interface ICommentsEmptyStateProps {
    text: string;
    iconRight?: ReactNode;
    iconLeft?: ReactNode;
    visibilityCell?: ComponentProps<typeof EmptyStateCell>;
}

function CommentsEmptyState({
    text,
    iconRight,
    iconLeft,
    visibilityCell,
}: ICommentsEmptyStateProps) {
    return (
        <div className="flex flex-col CommentsEmptyState gap-1 justify-center items-center text-center">
            <div className="flex flex-row CommentsEmptyState-Icons">
                <BdsIconComments size="lg" style={{ transform: "scaleX(-1)" }} />
                {iconLeft}
                {iconRight}
                <BdsIconComments size="lg" />
            </div>
            <BTText size="distinct-md" style={{ maxWidth: 250, lineHeight: "24px" }} text={text} />
            <div className="CommentsEmptyStateGrid">
                <EmptyStateCell
                    color="yellow"
                    icon={<BdsIconMention size="sm" />}
                    text="Use @mentions to get someone’s attention"
                />
                <EmptyStateCell
                    color="purple"
                    icon={<BdsIconAttach size="sm" />}
                    text="Add photos to share details easily"
                />
                <EmptyStateCell
                    color="green"
                    icon={<BdsIconThumbsUp size="sm" />}
                    text="React to comments to give quick feedback"
                />
                <EmptyStateCell
                    color="red"
                    icon={<BdsIconReplyClockwise size="sm" />}
                    text="Use replies to keep conversations tidy"
                />
                {visibilityCell && (
                    <EmptyStateCell
                        className="CommentsEmptyStateGrid-FullWidth"
                        {...visibilityCell}
                    />
                )}
            </div>
        </div>
    );
}
